/**
 * PromoAnalyticsAdmin.jsx — Elite Vacances
 * Estadísticas de clicks en promociones y banners (tiempo real).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAnalyticsElite } from "services/analitycs-black-friday";
import "../styles/PromoAnalyticsAdmin.css";
import "../styles/admin-theme.css";

const POLL_MS = 5000;
const ADMIN_HOME = "/8c4f9d17b6e245dca9814f7a5b6d3e2f";

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export default function PromoAnalyticsAdmin() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastSync, setLastSync] = useState(null);
  const [live, setLive] = useState(true);
  const [highlightIds, setHighlightIds] = useState(new Set());
  const prevClicksRef = useRef({});

  const fetchData = useCallback(async (isInitial = false) => {
    try {
      const { data } = await getAnalyticsElite();
      const list = Array.isArray(data) ? data : [];

      const changed = new Set();
      list.forEach((item) => {
        const prev = prevClicksRef.current[item.id];
        if (prev !== undefined && item.clicks > prev) {
          changed.add(item.id);
        }
        prevClicksRef.current[item.id] = item.clicks;
      });

      if (changed.size > 0) {
        setHighlightIds(changed);
        setTimeout(() => setHighlightIds(new Set()), 1800);
      }

      setRows(list);
      setLastSync(new Date());
      setError("");
    } catch (err) {
      console.error(err);
      setError("No se pudieron cargar las estadísticas. Reintentando…");
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  useEffect(() => {
    if (!live) return undefined;

    const tick = () => {
      if (document.visibilityState === "visible") {
        fetchData(false);
      }
    };

    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [live, fetchData]);

  const totalClicks = rows.reduce((sum, r) => sum + (Number(r.clicks) || 0), 0);
  const topPromo = rows.length
    ? [...rows].sort((a, b) => b.clicks - a.clicks)[0]
    : null;

  return (
    <div className="paa-wrap">
      <div className="paa-shell">
        <header className="paa-topbar">
          <button className="paa-back" onClick={() => navigate(ADMIN_HOME)}>
            ← Volver
          </button>
          <div className="paa-topbar__title">
            <span className="paa-topbar__icon">📊</span>
            <div>
              <h1 className="paa-topbar__h1">Estadísticas de Promociones</h1>
              <p className="paa-topbar__sub">Clicks en tiempo real — Elite Vacances</p>
            </div>
          </div>
          <div className="paa-topbar__actions">
            <button
              type="button"
              className={`paa-live-btn ${live ? "paa-live-btn--on" : ""}`}
              onClick={() => setLive((v) => !v)}
            >
              <span className={`paa-live-dot ${live ? "paa-live-dot--on" : ""}`} />
              {live ? "En vivo" : "Pausado"}
            </button>
            <button
              type="button"
              className="paa-refresh-btn"
              onClick={() => fetchData(false)}
            >
              Actualizar
            </button>
          </div>
        </header>

        <div className="paa-body">
          {error && <div className="paa-notice paa-notice--warn">{error}</div>}

          <div className="paa-stats">
            <div className="paa-stat">
              <span className="paa-stat__num">{rows.length}</span>
              <span className="paa-stat__lbl">Promociones</span>
            </div>
            <div className="paa-stat">
              <span className="paa-stat__num">{totalClicks.toLocaleString("es-CO")}</span>
              <span className="paa-stat__lbl">Clicks totales</span>
            </div>
            <div className="paa-stat">
              <span className="paa-stat__num paa-stat__num--sm">
                {topPromo ? topPromo.clicks.toLocaleString("es-CO") : "—"}
              </span>
              <span className="paa-stat__lbl">Mayor registro</span>
            </div>
            <div className="paa-stat">
              <span className="paa-stat__num paa-stat__num--sm">
                {lastSync ? formatDate(lastSync) : "—"}
              </span>
              <span className="paa-stat__lbl">Última sync</span>
            </div>
          </div>

          {loading ? (
            <div className="paa-empty">Cargando estadísticas…</div>
          ) : rows.length === 0 ? (
            <div className="paa-empty">No hay registros de clicks todavía.</div>
          ) : (
            <div className="paa-table-wrap">
              <table className="paa-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Promoción</th>
                    <th>Clicks</th>
                    <th>Creado el</th>
                    <th>Actualizado el</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr
                      key={item.id}
                      className={highlightIds.has(item.id) ? "paa-row--flash" : ""}
                    >
                      <td className="paa-col-id">{item.id}</td>
                      <td className="paa-col-promo">
                        {item.promocion?.replace(/^\[EV\]\s*/, "") || item.promocion}
                      </td>
                      <td className="paa-col-clicks">{item.clicks}</td>
                      <td className="paa-col-date">{formatDate(item.createdAt)}</td>
                      <td className="paa-col-date">{formatDate(item.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
